import { useState, useEffect, useMemo } from 'react';
import {
  ArrowRight, ArrowLeft, Check, CheckCircle2, Loader2,
  Mail, User, Building, TrendingUp, Download,
  ChevronsUpDown, Search, X, AlertTriangle, Brain, Zap, Dumbbell,
  Target, Award, Flame, Trophy, BarChart3, Lightbulb
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Cell, CartesianGrid, ResponsiveContainer,
  RadialBarChart, RadialBar, PolarAngleAxis
} from 'recharts';
import { generateArbeidsvoorwaardenPdf } from '@/lib/arbeidsvoorwaardenPdfGenerator';
import { arbeidsvoorwaardenCheckAPI } from '@/lib/api';
import BookingCta from '@/components/BookingCta';

const LOGO_ICON = 'https://customer-assets.emergentagent.com/job_buyer-match-26/artifacts/52px3160_zynq_logo_icon_e0dde9b0559f42738516da0422c8a0d0.png';

// ─── SBI 2026 sectoren (zelfde lijst als Vitaliteit Benchmark) ───
const SECTORS = [
  'A: Landbouw, bosbouw en visserij',
  'B: Delfstoffenwinning',
  'C: Industrie',
  'D: Energievoorziening',
  'E: Water- en afvalbeheer',
  'F: Bouwnijverheid',
  'G: Groot- en detailhandel',
  'H: Vervoer en opslag',
  'I: Horeca',
  'J: Informatie en communicatie',
  'K: Financiële instellingen',
  'L: Onroerend goed',
  'M: Specialistische zakelijke diensten',
  'N: Verhuur en overige zakelijke diensten',
  'O: Overheid en defensie',
  'P: Onderwijs',
  'Q: Gezondheids- en welzijnszorg',
  'R: Cultuur, sport en recreatie',
  'S: Overige dienstverlening',
  'T: Huishoudens als werkgever',
  'U: Extraterritoriale organisaties',
];

const FALLBACK_BENCHMARK = { mental: 85, flexibility: 90, physical: 65 };

// ─── Score & grade berekening (client-side, met server weights) ───
function calculateScore(checkedIds, items) {
  const mentalItems = items.filter(i => i.category === 'mental');
  const flexItems = items.filter(i => i.category === 'flexibility');
  const physItems = items.filter(i => i.category === 'physical');

  const sumWeights = (arr) => arr.reduce((s, i) => s + (i.weight || 0), 0);
  const sumChecked = (arr) =>
    arr.filter(i => checkedIds.includes(i.id)).reduce((s, i) => s + (i.weight || 0), 0);

  const mentalMax = sumWeights(mentalItems);
  const flexMax = sumWeights(flexItems);
  const physMax = sumWeights(physItems);
  const totalMax = mentalMax + flexMax + physMax;

  const mentalPts = sumChecked(mentalItems);
  const flexPts = sumChecked(flexItems);
  const physPts = sumChecked(physItems);
  const totalPts = mentalPts + flexPts + physPts;

  const percentage = totalMax > 0 ? Math.round((totalPts / totalMax) * 100) : 0;

  let grade, verdict;
  if (percentage >= 90) {
    grade = 'A+';
    verdict = 'Wereldklasse arbeidsvoorwaarden. Jullie zijn een talent-magneet voor Gen Z en behoren tot de top 5% werkgevers in Nederland. Focus nu op communicatie en employer branding om dit waar te maken in je recruitment-funnel.';
  } else if (percentage >= 75) {
    grade = 'A';
    verdict = 'Sterk pakket. Jullie scoren ruim boven gemiddeld en zijn aantrekkelijk voor het meeste toptalent. Een paar gerichte uitbreidingen op flexibiliteit of mentale support tilt jullie naar de absolute top.';
  } else if (percentage >= 60) {
    grade = 'B';
    verdict = 'Solide basis, maar mist scherpte op de zwaarstwegende thema\'s (flex + mentaal). Gen Z zal jullie aanbod als "oké, maar niet onderscheidend" ervaren. Investering in 2-3 high-impact benefits maakt het verschil.';
  } else if (percentage >= 40) {
    grade = 'C';
    verdict = 'Onder Gen Z verwachtingsniveau. Het pakket voldoet aan minimumvereisten maar mist de moderne flex- en mental-health voorzieningen die toptalent vandaag de dag vraagt. Prioriteit: hybride werken, mental health days en EAP.';
  } else {
    grade = 'D';
    verdict = 'Kritieke achterstand. In 2026 zal Gen Z jullie als werkgever niet serieus overwegen. Er is werk aan de winkel - start met de quick wins (mental health days, hybride werken, sportbudget) en bouw vanaf daar.';
  }

  return {
    grade, verdict, percentage,
    totalPoints: totalPts, maxPoints: totalMax,
    byCategory: {
      mental: {
        checked: mentalItems.filter(i => checkedIds.includes(i.id)).length,
        total: mentalItems.length,
        percentage: mentalMax > 0 ? Math.round((mentalPts / mentalMax) * 100) : 0,
      },
      flexibility: {
        checked: flexItems.filter(i => checkedIds.includes(i.id)).length,
        total: flexItems.length,
        percentage: flexMax > 0 ? Math.round((flexPts / flexMax) * 100) : 0,
      },
      physical: {
        checked: physItems.filter(i => checkedIds.includes(i.id)).length,
        total: physItems.length,
        percentage: physMax > 0 ? Math.round((physPts / physMax) * 100) : 0,
      },
    },
  };
}

// ─── Main component ───
export default function ArbeidsvoorwaardenCheckPage() {
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState('');
  const [configLoading, setConfigLoading] = useState(true);

  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);

  const [company, setCompany] = useState('');
  const [sector, setSector] = useState('');
  const [checked, setChecked] = useState([]);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  const [sectorOpen, setSectorOpen] = useState(false);

  // Force light mode voor embed-consistency
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains('dark');
    root.classList.remove('dark');
    return () => { if (hadDark) root.classList.add('dark'); };
  }, []);

  // Load config van backend
  useEffect(() => {
    let active = true;
    (async () => {
      setConfigLoading(true);
      try {
        const res = await arbeidsvoorwaardenCheckAPI.getConfig();
        if (!active) return;
        setConfig(res.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[Arbeidsvoorwaarden Check] Kon config niet laden:', e);
        if (active) setConfigError('Kon configuratie niet laden. Probeer de pagina te verversen.');
      } finally {
        if (active) setConfigLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const checklistItems = config?.checklist || [];
  const benchmark = config?.benchmark || FALLBACK_BENCHMARK;
  const accentColor = config?.accent_color || '#10b981';

  const scoreData = useMemo(
    () => calculateScore(checked, checklistItems),
    [checked, checklistItems]
  );

  const toggleItem = (id) => {
    setChecked(arr => arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id]);
  };

  const validateStep1 = () => {
    if (!company.trim()) return 'Vul de bedrijfsnaam in.';
    if (!sector) return 'Kies een sector.';
    return '';
  };
  const validateStep3 = () => {
    if (!contactName.trim()) return 'Vul je naam in.';
    if (!contactEmail.trim()) return 'Vul je zakelijke e-mailadres in.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim())) return 'Vul een geldig e-mailadres in.';
    return '';
  };

  const handleNext = async () => {
    setError('');
    if (step === 1) { const e = validateStep1(); if (e) { setError(e); return; } }
    if (step === 3) {
      const e = validateStep3(); if (e) { setError(e); return; }
      const payload = {
        company: company.trim(),
        sector,
        checked_items: checked,
        contact: { name: contactName.trim(), email: contactEmail.trim() },
      };
      // eslint-disable-next-line no-console
      console.log('[Arbeidsvoorwaarden Check 2026] Submission:', payload);
      setSubmitting(true);
      try {
        const res = await arbeidsvoorwaardenCheckAPI.submit(payload);
        const data = res.data || {};
        setSubmissionId(data.submission_id || null);
        // eslint-disable-next-line no-console
        console.log('[Arbeidsvoorwaarden Check 2026] Server response:', data);
        toast.success(data.message || 'Scorecard gegenereerd!');
        setStep(4);
      } catch (err) {
        const msg = err?.response?.data?.detail || 'Versturen mislukt. Probeer het opnieuw.';
        setError(msg);
        toast.error(msg);
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setStep(s => s + 1);
  };

  const handleBack = () => { setError(''); setStep(s => Math.max(0, s - 1)); };

  const handleRestart = () => {
    setStep(0); setError('');
    setCompany(''); setSector(''); setChecked([]);
    setContactName(''); setContactEmail('');
    setSubmissionId(null);
  };

  const handleDownloadPdf = async () => {
    try {
      await generateArbeidsvoorwaardenPdf({
        company, sector, contactName, contactEmail,
        checkedItems: checked,
        allItems: checklistItems,
        scoreData, benchmark,
        submissionId,
        cfg: config,
      });
      toast.success('PDF gedownload.');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Arbeidsvoorwaarden Check 2026] PDF generation failed:', err);
      toast.error('PDF kon niet worden gegenereerd. Probeer het opnieuw.');
    }
  };

  if (configLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-emerald-50/30">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Tool wordt geladen…</p>
        </div>
      </div>
    );
  }

  if (configError || !config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-emerald-50/30 px-4">
        <Card className="max-w-md w-full border-amber-200">
          <CardContent className="p-8 text-center space-y-3">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
            <h3 className="font-semibold text-lg">Tool niet beschikbaar</h3>
            <p className="text-sm text-slate-500">{configError || 'De Arbeidsvoorwaarden Check is momenteel niet beschikbaar.'}</p>
            <Button onClick={() => window.location.reload()} variant="outline">Opnieuw proberen</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (config.active === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-emerald-50/30 px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-2">
            <h3 className="font-semibold text-lg">Tool gepauzeerd</h3>
            <p className="text-sm text-slate-500">Deze check is momenteel niet actief. Probeer het later opnieuw.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50/30 py-8 px-4 text-slate-800" data-testid="arbeidsvoorwaarden-check-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <img src={LOGO_ICON} alt="Zynq" className="w-10 h-10 rounded-lg" />
            <div>
              <div className="font-bold text-lg leading-none text-slate-900">Zynq</div>
              <div className="text-xs text-slate-500">Arbeidsvoorwaarden Check 2026</div>
            </div>
          </div>
          {step > 0 && step < 4 && (
            <button
              onClick={handleRestart}
              className="text-xs text-slate-500 hover:text-emerald-700 underline"
            >
              Opnieuw beginnen
            </button>
          )}
        </div>

        {step > 0 && step < 4 && <ProgressBar step={step} accent={accentColor} />}

        {step === 0 && <IntroStep config={config} onStart={() => setStep(1)} />}

        {step === 1 && (
          <CompanyStep
            company={company} setCompany={setCompany}
            sector={sector}
            onSectorOpen={() => setSectorOpen(true)}
            onNext={handleNext} error={error}
          />
        )}

        {step === 2 && (
          <ChecklistStep
            checked={checked} onToggle={toggleItem}
            items={checklistItems}
            scoreData={scoreData}
            onNext={() => setStep(3)} onBack={handleBack}
          />
        )}

        {step === 3 && (
          <ContactStep
            name={contactName} setName={setContactName}
            email={contactEmail} setEmail={setContactEmail}
            submitting={submitting}
            ctaLabel={config.cta_submit || 'Toon scorecard'}
            onNext={handleNext} onBack={handleBack} error={error}
          />
        )}

        {step === 4 && (
          <DashboardStep
            company={company} sector={sector}
            checked={checked} allItems={checklistItems}
            scoreData={scoreData} benchmark={benchmark}
            submissionId={submissionId}
            showPdf={config.show_pdf_download !== false}
            onDownloadPdf={handleDownloadPdf}
            onRestart={handleRestart}
            bookingCfg={config}
          />
        )}

        <div className="mt-10 text-center text-xs text-slate-500">
          <p>Bron: Gen Z Talent Benchmark 2026 · Vitaliteit als missie. © Zynq</p>
        </div>
      </div>

      <SectorPicker open={sectorOpen} setOpen={setSectorOpen} value={sector} onChange={setSector} />
    </div>
  );
}

// ─── ProgressBar ───
function ProgressBar({ step }) {
  const labels = ['Bedrijf', 'Aanbod', 'Contact'];
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between max-w-md mx-auto">
        {labels.map((lbl, i) => {
          const idx = i + 1;
          const done = step > idx;
          const active = step === idx;
          return (
            <div key={lbl} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  done ? 'bg-emerald-600 text-white' :
                  active ? 'bg-emerald-600 text-white ring-4 ring-emerald-100' :
                  'bg-gray-200 text-slate-500'
                }`}>
                  {done ? <Check className="w-4 h-4" /> : idx}
                </div>
                <span className={`text-[10px] mt-1 font-medium ${active || done ? 'text-emerald-700' : 'text-slate-400'}`}>{lbl}</span>
              </div>
              {i < labels.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 mb-4 transition-all ${done ? 'bg-emerald-600' : 'bg-gray-200'}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Step 0: Intro ───
function IntroStep({ config, onStart }) {
  const bench = config.benchmark || FALLBACK_BENCHMARK;
  return (
    <div data-testid="step-intro" className="text-center">
      <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold mb-5">
        <Flame className="w-3.5 h-3.5" /> {config.intro_badge || 'War on Talent · Editie 2026'}
      </div>
      <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-4 text-slate-900">
        {config.intro_title || 'Is jouw arbeidsvoorwaarden-pakket Gen Z-proof?'}
      </h1>
      <p className="text-slate-600 max-w-2xl mx-auto mb-4 leading-relaxed">
        {config.intro_subtitle}
      </p>

      <div className="grid sm:grid-cols-3 gap-4 max-w-2xl mx-auto my-8">
        {[
          { Icon: Brain, label: 'Mentale Gezondheid', sub: `${bench.mental}% Gen Z eis` },
          { Icon: Zap, label: 'Flexibiliteit', sub: `${bench.flexibility}% Gen Z eis` },
          { Icon: Dumbbell, label: 'Fysiek & Omgeving', sub: `${bench.physical}% Gen Z eis` },
        ].map(({ Icon, label, sub }) => (
          <div key={label} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center mb-3 mx-auto">
              <Icon className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="font-semibold text-slate-900 text-sm">{label}</div>
            <div className="text-xs text-slate-500">{sub}</div>
          </div>
        ))}
      </div>

      <Button onClick={onStart} size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-8" data-testid="btn-start">
        {config.cta_start || 'Start de check'} <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
      <p className="text-xs text-slate-400 mt-3">Duurt 3 minuten · Direct Wellness Grade · Downloadbare PDF-scorecard</p>
    </div>
  );
}

// ─── Step 1: Bedrijf + Sector ───
function CompanyStep({ company, setCompany, sector, onSectorOpen, onNext, error }) {
  return (
    <div data-testid="step-company">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold tracking-tight mb-1 text-slate-900">Vertel ons over je organisatie</h2>
        <p className="text-slate-600 text-sm">Twee korte vragen om de scorecard te personaliseren.</p>
      </div>

      <Card className="border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6 md:p-8 space-y-6">
          <div>
            <Label htmlFor="company" className="flex items-center gap-2 mb-2">
              <Building className="w-4 h-4 text-emerald-600" /> Bedrijfsnaam
            </Label>
            <Input
              id="company" data-testid="input-company"
              placeholder="Bijv. Zynq B.V."
              value={company} onChange={(e) => setCompany(e.target.value)}
            />
          </div>

          <div>
            <Label className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-4 h-4 text-emerald-600" /> Sector (SBI 2026)
            </Label>
            <button
              type="button" data-testid="btn-sector"
              onClick={onSectorOpen}
              className="w-full flex items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm hover:border-emerald-400 transition-colors"
            >
              <span className={sector ? 'text-slate-900' : 'text-slate-400'}>
                {sector || 'Kies je sector…'}
              </span>
              <ChevronsUpDown className="w-4 h-4 text-slate-400" />
            </button>
            <p className="text-xs text-slate-500 mt-1">Doorzoekbaar · {SECTORS.length} sectoren beschikbaar</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end mt-6">
        <Button onClick={onNext} size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-next-1">
          Volgende <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

// ─── Step 2: Checklist ───
function ChecklistStep({ checked, onToggle, items, scoreData, onNext, onBack }) {
  const categories = [
    { key: 'mental', label: 'Mentale Gezondheid', Icon: Brain, color: 'rose', desc: 'Wat bieden jullie aan op het gebied van mentale steun?' },
    { key: 'flexibility', label: 'Flexibiliteit', Icon: Zap, color: 'sky', desc: 'In hoeverre is autonomie en flex onderdeel van jullie cultuur?' },
    { key: 'physical', label: 'Fysiek & Omgeving', Icon: Dumbbell, color: 'amber', desc: 'Welke fysieke vitaliteits-voorzieningen zijn er?' },
  ];

  return (
    <div data-testid="step-checklist">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold tracking-tight mb-1 text-slate-900">Wat bieden jullie nu aan?</h2>
        <p className="text-slate-600 text-sm">Vink aan welke arbeidsvoorwaarden jullie al hebben. Mentale steun &amp; flex wegen het zwaarst in 2026.</p>
      </div>

      <Card className="border-emerald-200 shadow-sm bg-gradient-to-r from-emerald-50 to-white mb-5">
        <CardContent className="py-3 px-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold text-lg">
              {scoreData.grade}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">Voorlopige score</div>
              <div className="text-sm font-semibold text-slate-800">{scoreData.percentage}% · {checked.length}/{items.length} aangevinkt</div>
            </div>
          </div>
          <div className="text-xs text-slate-500 hidden sm:block">Score update live</div>
        </CardContent>
      </Card>

      {categories.map(cat => {
        const catItems = items.filter(i => i.category === cat.key);
        const catScore = scoreData.byCategory[cat.key];
        const colorClasses = {
          rose: { bg: 'bg-rose-100', text: 'text-rose-600', border: 'border-rose-200' },
          sky: { bg: 'bg-sky-100', text: 'text-sky-600', border: 'border-sky-200' },
          amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
        }[cat.color];

        return (
          <Card key={cat.key} className="border-slate-200 shadow-sm bg-white mb-4">
            <CardContent className="p-5 md:p-6">
              <div className="flex items-start justify-between gap-3 mb-1">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl ${colorClasses.bg} flex items-center justify-center flex-shrink-0`}>
                    <cat.Icon className={`w-5 h-5 ${colorClasses.text}`} />
                  </div>
                  <div>
                    <h3 className="font-bold text-base text-slate-900 flex items-center gap-2 flex-wrap">
                      {cat.label}
                      {cat.key !== 'physical' && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                          Top-prioriteit Gen Z
                        </span>
                      )}
                    </h3>
                    <p className="text-xs text-slate-500">{cat.desc}</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-xs text-slate-500">Score</div>
                  <div className="font-bold text-slate-900">{catScore.checked}/{catScore.total}</div>
                </div>
              </div>

              <div className="mt-4 grid sm:grid-cols-2 gap-2">
                {catItems.map(it => {
                  const on = checked.includes(it.id);
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => onToggle(it.id)}
                      data-testid={`item-${it.id}`}
                      className={`group text-left flex items-start gap-3 p-3 rounded-lg border-2 transition-all ${
                        on
                          ? `${colorClasses.border} bg-gradient-to-br from-white to-slate-50 shadow-sm`
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className={`mt-0.5 w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-all ${
                        on
                          ? 'bg-emerald-600 ring-2 ring-emerald-200'
                          : 'border-2 border-slate-300 group-hover:border-slate-400'
                      }`}>
                        {on && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-semibold ${on ? 'text-slate-900' : 'text-slate-700'}`}>
                          {it.label}
                        </div>
                        <div className="text-[11px] text-slate-500 leading-snug mt-0.5">
                          {it.desc}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}

      <div className="flex justify-between mt-6">
        <Button onClick={onBack} variant="outline" size="lg" data-testid="btn-back-2">
          <ArrowLeft className="w-4 h-4 mr-2" /> Terug
        </Button>
        <Button onClick={onNext} size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-next-2">
          Volgende <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

// ─── Step 3: Contact ───
function ContactStep({ name, setName, email, setEmail, submitting, ctaLabel, onNext, onBack, error }) {
  return (
    <div data-testid="step-contact">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold tracking-tight mb-1 text-slate-900">Bijna klaar - waar mogen we de scorecard sturen?</h2>
        <p className="text-slate-600 text-sm">Je Wellness Grade verschijnt direct in beeld en is ook downloadbaar als PDF.</p>
      </div>

      <Card className="border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6 md:p-8 space-y-6">
          <div>
            <Label htmlFor="name" className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-emerald-600" /> Jouw naam
            </Label>
            <Input id="name" data-testid="input-name" placeholder="Voor- en achternaam"
              value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="email" className="flex items-center gap-2 mb-2">
              <Mail className="w-4 h-4 text-emerald-600" /> Zakelijk e-mailadres
            </Label>
            <Input id="email" data-testid="input-email" type="email" placeholder="jij@bedrijf.nl"
              value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}
            </div>
          )}

          <p className="text-xs text-slate-500">
            Door op &quot;{ctaLabel}&quot; te klikken ga je akkoord met het opslaan van je gegevens ten behoeve van dit rapport.
          </p>
        </CardContent>
      </Card>

      <div className="flex justify-between mt-6">
        <Button onClick={onBack} variant="outline" size="lg" data-testid="btn-back-3">
          <ArrowLeft className="w-4 h-4 mr-2" /> Terug
        </Button>
        <Button onClick={onNext} size="lg" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-submit">
          {submitting ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Versturen…</>
          ) : (
            <>{ctaLabel} <CheckCircle2 className="w-4 h-4 ml-2" /></>
          )}
        </Button>
      </div>
    </div>
  );
}

// ─── Step 4: Dashboard ───
function DashboardStep({ company, sector, checked, allItems, scoreData, benchmark, submissionId, showPdf, onDownloadPdf, onRestart, bookingCfg }) {
  const missing = allItems.filter(it => !checked.includes(it.id));
  const sortedMissing = [...missing].sort((a, b) => (b.weight || 0) - (a.weight || 0));

  const gradeColor =
    scoreData.grade === 'A+' ? 'from-emerald-500 to-teal-600' :
    scoreData.grade === 'A' ? 'from-emerald-500 to-green-600' :
    scoreData.grade === 'B' ? 'from-lime-500 to-emerald-500' :
    scoreData.grade === 'C' ? 'from-amber-500 to-orange-500' :
    'from-rose-500 to-red-600';

  const compareData = [
    { name: 'Mentale Gezondheid', jouw: scoreData.byCategory.mental.percentage, genz: benchmark.mental },
    { name: 'Flexibiliteit', jouw: scoreData.byCategory.flexibility.percentage, genz: benchmark.flexibility },
    { name: 'Fysiek & Omgeving', jouw: scoreData.byCategory.physical.percentage, genz: benchmark.physical },
  ];

  return (
    <div data-testid="step-dashboard" className="space-y-6">
      <Card className="border-emerald-200 shadow-sm bg-gradient-to-br from-white to-emerald-50/40">
        <CardContent className="p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold mb-2">
                <Award className="w-3.5 h-3.5" /> Arbeidsvoorwaarden Scorecard
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900">{company}</h2>
              <p className="text-sm text-slate-600 mt-1">{sector}</p>
              <p className="text-xs text-slate-500 mt-1">{new Date().toLocaleDateString('nl-NL')}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {showPdf && (
                <Button onClick={onDownloadPdf} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-download-pdf">
                  <Download className="w-4 h-4 mr-2" /> PDF downloaden
                </Button>
              )}
              <Button onClick={onRestart} variant="outline">Opnieuw</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className={`border-2 shadow-lg bg-gradient-to-br ${gradeColor} text-white border-transparent`}>
        <CardContent className="p-6 md:p-8">
          <div className="grid md:grid-cols-3 gap-6 items-center">
            <div className="text-center md:text-left">
              <div className="text-sm font-semibold uppercase tracking-wide text-white/80 mb-2">Wellness Grade</div>
              <div className="text-7xl md:text-8xl font-black leading-none" data-testid="wellness-grade">{scoreData.grade}</div>
              <div className="mt-2 text-lg font-semibold">{scoreData.percentage}%</div>
              <div className="text-xs text-white/80">{Math.round(scoreData.totalPoints)} / {Math.round(scoreData.maxPoints)} punten</div>
            </div>
            <div className="md:col-span-2">
              <div className="text-sm font-semibold uppercase tracking-wide text-white/80 mb-2">Wat betekent dit?</div>
              <p className="text-sm md:text-base leading-relaxed">{scoreData.verdict}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <BookingCta config={bookingCfg} variant="emerald" />

      <div className="grid md:grid-cols-3 gap-4">
        <CategoryGauge
          title="Mentale Gezondheid"
          Icon={Brain}
          percentage={scoreData.byCategory.mental.percentage}
          target={benchmark.mental}
          checked={scoreData.byCategory.mental.checked}
          total={scoreData.byCategory.mental.total}
          color="#f43f5e"
        />
        <CategoryGauge
          title="Flexibiliteit"
          Icon={Zap}
          percentage={scoreData.byCategory.flexibility.percentage}
          target={benchmark.flexibility}
          checked={scoreData.byCategory.flexibility.checked}
          total={scoreData.byCategory.flexibility.total}
          color="#0ea5e9"
        />
        <CategoryGauge
          title="Fysiek & Omgeving"
          Icon={Dumbbell}
          percentage={scoreData.byCategory.physical.percentage}
          target={benchmark.physical}
          checked={scoreData.byCategory.physical.checked}
          total={scoreData.byCategory.physical.total}
          color="#f59e0b"
        />
      </div>

      <Card className="border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6 md:p-8">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div>
              <h3 className="font-bold text-lg text-slate-900">Vergelijking met Gen Z Benchmark 2026</h3>
              <p className="text-xs text-slate-500">Jouw aanbod (%) versus wat het instromende toptalent als harde eis stelt.</p>
            </div>
            <Target className="w-5 h-5 text-emerald-600" />
          </div>

          <div className="h-72" data-testid="chart-comparison">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={compareData} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: '#475569', fontSize: 12 }} axisLine={{ stroke: '#cbd5e1' }} />
                <YAxis tick={{ fill: '#475569', fontSize: 12 }} axisLine={{ stroke: '#cbd5e1' }} unit="%" domain={[0, 100]} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                  formatter={(v, name) => [`${v}%`, name === 'jouw' ? 'Jouw aanbod' : 'Gen Z eis']}
                />
                <Bar dataKey="jouw" name="jouw" radius={[6, 6, 0, 0]}>
                  {compareData.map((d, i) => {
                    const meets = d.jouw >= d.genz;
                    const color = meets ? '#10b981' : d.jouw >= d.genz * 0.6 ? '#f59e0b' : '#ef4444';
                    return <Cell key={i} fill={color} />;
                  })}
                </Bar>
                <Bar dataKey="genz" name="genz" radius={[6, 6, 0, 0]} fill="#475569" fillOpacity={0.85} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            {compareData.map((d) => {
              const gap = d.genz - d.jouw;
              const meets = d.jouw >= d.genz;
              return (
                <div key={d.name} className={`rounded-xl border p-3 ${meets ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">{d.name}</div>
                  <div className="text-lg font-bold text-slate-900">
                    {d.jouw}% <span className="text-xs text-slate-400 font-normal">vs {d.genz}% eis</span>
                  </div>
                  <p className={`text-xs mt-1 leading-relaxed ${meets ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {meets ? `Voldoet ruim aan Gen Z eis (${d.jouw - d.genz}pp boven).` : `${gap}pp te kort op Gen Z benchmark.`}
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm bg-white" data-testid="gaps-section">
        <CardContent className="p-6 md:p-8">
          <div className="flex items-center justify-between mb-1 gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-900">Gaps to Close</h3>
                <p className="text-xs text-slate-500">Direct toepasbaar advies om Gen Z talent aan te trekken.</p>
              </div>
            </div>
            <div className="text-xs text-slate-500">{sortedMissing.length} gap{sortedMissing.length !== 1 ? 's' : ''}</div>
          </div>

          {sortedMissing.length === 0 ? (
            <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
              <Trophy className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
              <p className="font-bold text-slate-900">Wereldklasse pakket - geen gaps!</p>
              <p className="text-sm text-slate-600 mt-1">Jullie bieden alle ge-benchmarkte voorwaarden. Focus nu op communicatie naar talent.</p>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {sortedMissing.slice(0, 6).map((it) => {
                const catLabel = it.category === 'mental' ? 'Mentaal' : it.category === 'flexibility' ? 'Flex' : 'Fysiek';
                const catColor = it.category === 'mental' ? 'bg-rose-100 text-rose-700' :
                  it.category === 'flexibility' ? 'bg-sky-100 text-sky-700' :
                  'bg-amber-100 text-amber-700';
                return (
                  <div key={it.id} className="flex items-start gap-3 p-4 rounded-lg border border-slate-200 bg-gradient-to-r from-white to-slate-50/60">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${catColor}`}>
                        {catLabel}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-slate-900">{it.label}</div>
                      <p className="text-xs text-slate-600 mt-1 leading-relaxed">{it.advice}</p>
                    </div>
                  </div>
                );
              })}
              {sortedMissing.length > 6 && (
                <p className="text-xs text-slate-500 italic text-center pt-2">
                  + {sortedMissing.length - 6} extra verbeterpunten in het PDF-rapport.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-2 border-emerald-300 shadow-lg bg-gradient-to-br from-emerald-600 to-teal-700 text-white">
        <CardContent className="p-6 md:p-8">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-xl">Win de War on Talent</h3>
              <p className="text-sm text-emerald-50 mt-2 leading-relaxed">
                We bouwen samen met jullie HR/People-team een arbeidsvoorwaarden-pakket waar Gen&nbsp;Z toptalent niet omheen kan. Plan een vrijblijvend strategiegesprek - we delen sector-specifieke best practices en de quick wins waarmee je deze week al kunt starten.
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                {showPdf && (
                  <Button onClick={onDownloadPdf} className="bg-white text-emerald-700 hover:bg-emerald-50" data-testid="btn-download-pdf-bottom">
                    <Download className="w-4 h-4 mr-2" /> Download volledige Scorecard (PDF)
                  </Button>
                )}
                <a
                  href="mailto:hello@zynq.nl?subject=Strategiegesprek%20Arbeidsvoorwaarden%202026"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-emerald-800/40 hover:bg-emerald-800/60 text-white text-sm font-semibold border border-white/20"
                >
                  <Mail className="w-4 h-4" /> Plan strategiegesprek
                </a>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {submissionId && (
        <p className="text-[11px] text-slate-400 text-center">Rapport-ID: {submissionId}</p>
      )}
    </div>
  );
}

// ─── CategoryGauge ───
function CategoryGauge({ title, Icon, percentage, target, checked, total, color }) {
  const data = [{ name: title, value: percentage, fill: color }];
  const meets = percentage >= target;
  return (
    <Card className="border-slate-200 shadow-sm bg-white">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <Icon className="w-4 h-4 text-slate-500" />
          <h4 className="font-semibold text-sm text-slate-700">{title}</h4>
        </div>
        <div className="h-28 relative">
          <ResponsiveContainer width="100%" height="100%">
            <RadialBarChart innerRadius="70%" outerRadius="100%" data={data} startAngle={180} endAngle={0}>
              <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
              <RadialBar background={{ fill: '#f1f5f9' }} dataKey="value" cornerRadius={10} />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-end pb-1">
            <div className="text-2xl font-bold text-slate-900">{percentage}%</div>
            <div className="text-[10px] uppercase tracking-wide text-slate-400">{checked}/{total} items</div>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs mt-3 pt-3 border-t border-slate-100">
          <span className="text-slate-500">Gen Z eis: <span className="font-semibold text-slate-700">{target}%</span></span>
          {meets ? (
            <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold">
              <CheckCircle2 className="w-3 h-3" /> Voldoet
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">
              <AlertTriangle className="w-3 h-3" /> Aandacht
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── SectorPicker ───
function SectorPicker({ open, setOpen, value, onChange }) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SECTORS;
    return SECTORS.filter(k => k.toLowerCase().includes(q));
  }, [query]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5">
          <DialogTitle>Kies je sector</DialogTitle>
          <DialogDescription>Selecteer de SBI-categorie die het beste past bij je organisatie.</DialogDescription>
        </DialogHeader>
        <div className="px-5 pt-3 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              autoFocus data-testid="input-sector-search"
              placeholder="Zoek sector (bv. zorg, IT, onderwijs)…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
            />
            {query && (
              <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100">
                <X className="w-3.5 h-3.5 text-slate-400" />
              </button>
            )}
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto px-2 pb-3">
          {filtered.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">Geen sector gevonden.</div>
          ) : (
            filtered.map((k) => {
              const selected = k === value;
              return (
                <button
                  key={k}
                  onClick={() => { onChange(k); setOpen(false); setQuery(''); }}
                  data-testid={`sector-option-${k.split(':')[0]}`}
                  className={`w-full text-left flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                    selected ? 'bg-emerald-50 text-emerald-700' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <span>{k}</span>
                  {selected && <Check className="w-4 h-4 text-emerald-600" />}
                </button>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
